import React, { useState } from 'react';
import styled from 'styled-components';
import { object, string } from 'yup';
import type { ObjectSchema } from 'yup';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useTranslation from 'hooks/use-translation';
import useErnie from 'shared/hooks/use-ernie';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useSendLoginCode } from 'src/state/actions/users';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS } from 'shared/constants/turnstile';
import type { TurnstileAction } from 'shared/constants/turnstile';

import { Button } from 'components/core';
import { FormTextInput } from '../../form-inputs';
import Form from '../form';
import { emailRegEx } from '../form-utils';

type EmailFormValues = {
  email: string;
};

/**
 * Validation schema for email entry form
 */
const emailFormValidation = (t: (key: string, defaultValue: string) => string): ObjectSchema<EmailFormValues> =>
  object().shape({
    email: string()
      .required(t('form.emailRequired', 'Email is required'))
      .matches(emailRegEx, t('form.validEmail', 'Please enter a valid email address')),
  });

export type EmailEntryFormProps = {
  onCodeSent: (email: string) => void;
  onBack?: () => void;
  dispensaryId?: string;
};

/**
 * Email entry form component for the first step of email code login.
 * User enters their email address and a verification code is sent.
 */
export function EmailEntryForm({ onCodeSent, onBack, dispensaryId }: EmailEntryFormProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const flags = useFlags();
  const { t } = useTranslation();
  const { dispensary } = useDispensary();
  const showErnie = useErnie();
  const sendLoginCode = useSendLoginCode();

  const { TurnstileComponent, executeAndWaitForToken } = useTurnstile({
    size: 'flexible',
    action: TURNSTILE_ACTIONS.LOGIN as TurnstileAction,
  });

  const handleSubmit = async ({ email }: { email: string }): Promise<void> => {
    try {
      setLoading(true);
      let turnstileToken = '';

      // Get Turnstile token if enabled
      if (flags['growth.ecomm.login-turnstile-enable.rollout']) {
        try {
          turnstileToken = await executeAndWaitForToken({ successDelayMs: 1000, keepMounted: true });
        } catch (error) {
          showErnie(t('emailCodeLogin.turnstileError', 'Error validating your request. Please try again.'), 'danger');
          setLoading(false);
          return;
        }
      }

      const result = await sendLoginCode({
        email,
        dispensaryId: dispensaryId ?? dispensary?.id,
        turnstileToken,
      });

      if (result.success) {
        showErnie(t('emailCodeLogin.codeSent', 'A verification code has been sent to your email.'), 'success');
        onCodeSent(email);
      } else {
        const errorMessage =
          result.error?.graphQLErrors?.[0]?.extensions?.errors?.[0]?.detail ||
          result.error?.message ||
          t('emailCodeLogin.sendCodeError', 'Unable to send verification code. Please try again.');
        showErnie(errorMessage, 'danger');
      }
    } catch (err) {
      console.error(err);
      showErnie(t('emailCodeLogin.genericError', 'Something went wrong. Please try again.'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} validationSchema={emailFormValidation(t)}>
      <Description>
        {t('emailCodeLogin.enterEmailDescription', "Enter your email and we'll send you a code to log in.")}
      </Description>

      <FormTextInput
        data-testid='email-code-login-email-input'
        name='email'
        label={t('emailCodeLogin.emailLabel', 'Email')}
        variant='filled'
        autoComplete='email'
        type='email'
        required
      />

      {flags['growth.ecomm.login-turnstile-enable.rollout'] && <TurnstileComponent />}

      <ButtonContainer>
        {onBack && (
          <BackButton type='button' onClick={onBack} data-testid='email-code-login-back-button'>
            {t('emailCodeLogin.back', 'Back')}
          </BackButton>
        )}

        <SubmitButton
          variant='flora'
          loading={loading}
          disabled={loading}
          type='submit'
          size='medium'
          data-testid='email-code-login-send-code-button'
        >
          {t('emailCodeLogin.sendCode', 'Send code')}
        </SubmitButton>
      </ButtonContainer>
    </Form>
  );
}

const Description = styled.p`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  line-height: ${20 / 14};
  margin-bottom: 20px;
  margin-top: 0;
`;

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
`;

const BackButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled(Button)`
  background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  min-width: 100px;
`;

export default EmailEntryForm;
